/**
 * Liste des étudiants en ligne (cross-niveau).
 * - Tabs : Ma classe / Tous
 * - Recherche par nom/matricule
 * - Clic sur un étudiant → ouverture conversation 1-1
 * - Auto-refresh toutes les 30s
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, MessageSquare, Users, Loader2, RefreshCw,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, ChatEtudiantSummary } from '../services/portailApi';
import PhotoLightbox from '../../components/ui/PhotoLightbox';

type Scope = 'ma_classe' | 'tous';

export default function EnLigne() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<Scope>('ma_classe');
  const [items, setItems] = useState<ChatEtudiantSummary[]>([]);
  const [maClasse, setMaClasse] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [openingConv, setOpeningConv] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [photoModal, setPhotoModal] = useState<ChatEtudiantSummary | null>(null);
  const [invisible, setInvisible] = useState(false);
  const refreshTimer = useRef<number | null>(null);

  const load = async (currentScope: Scope, query: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await portailApi.etudiantsEnLigne(currentScope, query) as any;
      setItems(r.results);
      setMaClasse(r.ma_classe);
      setInvisible(Boolean(r.invisible));
      setLastRefresh(new Date());
    } catch (e: any) {
      if (e?.status === 401) {
        portailApi.clearToken();
        navigate('/portail/login', { replace: true });
        return;
      }
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(scope, q);
    if (refreshTimer.current) window.clearInterval(refreshTimer.current);
    refreshTimer.current = window.setInterval(() => load(scope, q), 30000);
    return () => {
      if (refreshTimer.current) window.clearInterval(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Debounce sur recherche
  useEffect(() => {
    const t = setTimeout(() => load(scope, q), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const ouvrirChat = async (etu: ChatEtudiantSummary) => {
    const id = etu.matricule_reel || etu.matricule;
    setOpeningConv(id);
    try {
      const r = await portailApi.chatOuvrirDirecte(id);
      // Fix 2026-08-29 : /portail/messages-camarades est un redirect qui PERD
      // la query string ?conv=X, la conversation ne s'ouvrait donc jamais.
      // On navigue directement vers la vraie URL avec query préservée.
      navigate(`/portail/messagerie?tab=camarades&conv=${r.conversation_id}`);
    } catch (e: any) {
      alert(e?.message || 'Impossible d\'ouvrir la conversation');
    } finally {
      setOpeningConv(null);
    }
  };

  const groupesParClasse = useMemo(() => {
    const m = new Map<string, ChatEtudiantSummary[]>();
    items.forEach((e) => {
      const k = e.classe || '—';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    });
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <PortailLayout showHeader title="Étudiants en ligne" fullWidth>
      <div className="space-y-3">
        <button
          onClick={() => navigate('/portail/accueil')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        {/* Tabs */}
        <div className="flex bg-gray-900 border border-gray-800 rounded-lg p-1 gap-1">
          <button
            onClick={() => setScope('ma_classe')}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
              scope === 'ma_classe'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Ma classe {maClasse && <span className="opacity-70">· {maClasse}</span>}
          </button>
          <button
            onClick={() => setScope('tous')}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
              scope === 'tous'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Tout l'institut
          </button>
        </div>

        {/* Recherche */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un nom ou un matricule..."
            className="w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Stats + refresh */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span>{items.length} étudiant{items.length > 1 ? 's' : ''} en ligne</span>
          </span>
          <button
            onClick={() => load(scope, q)}
            className="inline-flex items-center gap-1 hover:text-gray-300"
            title="Rafraîchir"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            {lastRefresh && <span>{lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>}
          </button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading && items.length === 0 && (
          <div className="text-center py-12">
            <Loader2 className="inline-block animate-spin text-blue-400" size={28} />
          </div>
        )}

        {!loading && invisible && (
          <div className="bg-orange-900/30 border border-orange-700 rounded-xl p-5 text-center space-y-3">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-700/40 border border-orange-600">
              <Users className="text-orange-300" size={22} />
            </div>
            <div className="font-semibold text-sm text-orange-200">
              Vous êtes en mode invisible
            </div>
            <p className="text-xs text-orange-300/90 leading-relaxed max-w-xs mx-auto">
              Pour voir les autres étudiants en ligne, vous devez d'abord
              accepter d'être visible vous-même. Désactivez le « Mode invisible »
              dans Réglages.
            </p>
            <button
              onClick={() => navigate('/portail/reglages')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded"
            >
              Aller dans Réglages →
            </button>
          </div>
        )}

        {!loading && !invisible && items.length === 0 && !error && (
          <div className="text-center py-12 text-gray-500 text-sm space-y-2">
            <Users className="inline-block text-gray-700" size={32} />
            <div>
              {scope === 'ma_classe'
                ? `Personne de ${maClasse || 'votre classe'} n'est connecté pour le moment.`
                : 'Personne n\'est en ligne pour le moment.'}
            </div>
            {scope === 'ma_classe' && (
              <button
                onClick={() => setScope('tous')}
                className="text-blue-400 hover:underline text-xs"
              >
                Voir tout l'institut →
              </button>
            )}
          </div>
        )}

        {/* Liste groupée par classe en mode 'tous', sinon flat */}
        {scope === 'tous' && items.length > 0 ? (
          groupesParClasse.map(([classe, etus]) => (
            <div key={classe} className="space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-1">
                {classe} <span className="text-gray-600 normal-case font-normal">· {etus.length}</span>
              </div>
              {etus.map((e) => (
                <EtudiantRow
                  key={e.matricule_reel || e.matricule}
                  etu={e}
                  loading={openingConv === (e.matricule_reel || e.matricule)}
                  onOpen={() => ouvrirChat(e)}
                  onAvatar={() => setPhotoModal(e)}
                />
              ))}
            </div>
          ))
        ) : (
          <div className="space-y-1.5">
            {items.map((e) => (
              <EtudiantRow
                key={e.matricule_reel || e.matricule}
                etu={e}
                loading={openingConv === (e.matricule_reel || e.matricule)}
                onOpen={() => ouvrirChat(e)}
                onAvatar={() => setPhotoModal(e)}
              />
            ))}
          </div>
        )}
      </div>
      {/* Lightbox photo (fix 2026-08-31 : composant universel) */}
      <PhotoLightbox
        url={photoModal?.photo_url || null}
        label={photoModal ? `${photoModal.prenoms || ''} ${photoModal.nom || ''}`.trim() : undefined}
        hint={photoModal?.classe || undefined}
        onClose={() => setPhotoModal(null)}
      />
    </PortailLayout>
  );
}

const EtudiantRow: React.FC<{
  etu: ChatEtudiantSummary;
  loading: boolean;
  onOpen: () => void;
  onAvatar?: () => void;
}> = ({ etu, loading, onOpen, onAvatar }) => {
  return (
    <div
      className="w-full flex items-center gap-3 px-3 py-2.5 bg-gray-900 border border-gray-800 hover:border-blue-700 rounded-lg transition-colors"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onAvatar?.(); }}
        className="relative flex-shrink-0 group"
        aria-label="Voir la photo"
        disabled={!etu.photo_url}
      >
        {etu.photo_url ? (
          <img src={etu.photo_url} alt="" className="w-10 h-10 rounded-full object-cover ring-0 group-hover:ring-2 ring-blue-400 transition" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
            {(etu.prenoms?.[0] || '') + (etu.nom?.[0] || '')}
          </div>
        )}
        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-gray-900" />
      </button>
      <button
        type="button"
        onClick={onOpen}
        disabled={loading}
        className="flex-1 text-left min-w-0 disabled:opacity-60"
      >
        <div className="text-sm font-semibold text-gray-100 truncate">
          {etu.prenoms} {etu.nom}
        </div>
        <div className="text-[11px] text-gray-500 truncate">
          {etu.matricule_masque ? '' : `${etu.matricule}${etu.classe ? ' · ' : ''}`}{etu.classe}
        </div>
      </button>
      {loading ? (
        <Loader2 size={14} className="text-blue-400 animate-spin flex-shrink-0" />
      ) : (
        <MessageSquare size={14} className="text-blue-400 flex-shrink-0" />
      )}
    </div>
  );
};

const PhotoModal: React.FC<{
  etu: ChatEtudiantSummary;
  onClose: () => void;
}> = ({ etu, onClose }) => {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-white/80 hover:text-white text-3xl leading-none"
        aria-label="Fermer"
      >×</button>
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-3 max-w-md w-full">
        {etu.photo_url ? (
          <img src={etu.photo_url} alt="" className="max-w-full max-h-[80vh] rounded-lg object-contain" />
        ) : (
          <div className="w-48 h-48 rounded-full bg-blue-600 text-white flex items-center justify-center text-4xl font-bold">
            {(etu.prenoms?.[0] || '') + (etu.nom?.[0] || '')}
          </div>
        )}
        <div className="text-center text-white">
          <div className="font-semibold text-base">{etu.prenoms} {etu.nom}</div>
          <div className="text-xs text-gray-300 mt-0.5">{etu.classe || ''}{etu.option ? ` · ${etu.option}` : ''}</div>
        </div>
      </div>
    </div>
  );
};
